/**
 * EditDeliveryLineModal — inline correction UI for a single stock unit
 * on the Purchases tab.
 *
 * Why it exists:
 *   Staff make data-entry mistakes on the "Log Supplier Delivery" flow —
 *   most commonly typing a per-meter rate into the "cost" field when
 *   that field expects the per-unit (per-roll) TOTAL. Without an edit
 *   path, the only fix was creating a duplicate delivery, which
 *   inflated stock. This modal is that edit path.
 *
 * What can be edited (mirrors PATCH /api/stock/units/{unit_id}):
 *   - cost / cost_gross (net or VAT-inclusive)
 *   - vat_treatment
 *   - vendor_name
 *   - purchase_date
 *   - initial_quantity   (only for units still whole — refused by BE
 *                          if the roll has been partially consumed)
 *
 * Live cost-per-unit-of-measure preview:
 *   The whole reason for the recent Chameleon-film cost bug was
 *   ambiguity between "cost per meter" vs "cost per roll". This modal
 *   permanently kills that ambiguity by rendering, in real time as the
 *   user types:
 *       "= €X.XX per meter   (over Y meters)"
 *   below the cost field. Same live-preview treatment now sits in the
 *   Log Supplier Delivery modal itself (upstream prevention) — see
 *   StockInventorySection.jsx.
 *
 * Downstream re-costing:
 *   The backend endpoint automatically invalidates every job_log
 *   materials_used snapshot that referenced this unit before the
 *   correction. The next nightly reconcile (or an admin-triggered
 *   "Run Now") will re-derive them from the corrected values.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertCircle, Trash2, Zap } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";
import { showDeleteUndoToast } from "./deliveryUndoToast";
import CostSanityWarning from "./CostSanityWarning";
import { SuggestInput } from "./SuggestInput";

const VAT_OPTIONS = [
  { value: "standard", label: "Standard (23% Ireland)" },
  { value: "eu_reverse_charge", label: "EU Reverse Charge" },
  { value: "no_vat", label: "No VAT" },
];

// Human unit label for the live preview. Same mapping as the delivery
// modal so both stay in sync.
const perUnitLabel = (baseUnit) => {
  if (baseUnit === "cm") return "meter";
  if (baseUnit === "ml") return "ml";
  return "unit";
};

// If the product is measured in cm, users enter meters. Convert both
// ways so the input field always shows the intuitive number.
const toDisplayQty = (raw, baseUnit) => {
  if (raw == null || raw === "") return "";
  return baseUnit === "cm" ? String(raw / 100) : String(raw);
};

const toStorageQty = (raw, baseUnit) => {
  const n = parseFloat(raw);
  if (isNaN(n)) return null;
  return baseUnit === "cm" ? n * 100 : n;
};

const EditDeliveryLineModal = ({ open, onClose, line, onSaved, onDeleted }) => {
  // `line` shape (from purchases endpoint): { unit_id, product_name,
  //   cost, cost_gross, vat_treatment, vendor_name, purchase_date,
  //   initial_quantity, base_unit, consumed (bool), ... }
  const [cost, setCost] = useState("");
  const [costMode, setCostMode] = useState("net"); // "net" | "gross"
  const [vatTreatment, setVatTreatment] = useState("standard");
  const [vendorName, setVendorName] = useState("");
  // Supplier names already on record, so the regulars can be picked
  // instead of re-typed. Fetched here rather than prop-drilled: this
  // modal is opened from the Invoices tab, not the Inventory tab.
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [initialQtyDisplay, setInitialQtyDisplay] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Post-save "Re-cost affected jobs now" button state — only shown when
  // this unit has been consumed by at least one job. Independent from
  // saving so the admin can click it after the initial save.
  const [recosting, setRecosting] = useState(false);
  const [recostSummary, setRecostSummary] = useState(null);
  // Cost-baseline for the sanity check — fetched once per modal open.
  const [baseline, setBaseline] = useState(null);
  const [sanityOverride, setSanityOverride] = useState(false);

  const baseUnit = line?.base_unit || "unit";
  const unitLabel = perUnitLabel(baseUnit);
  const canEditQty = !line?.consumed;  // BE will refuse; UI hides the field

  // Hydrate form when line changes / modal opens.
  useEffect(() => {
    if (!open || !line) return;
    setCost(line.cost != null ? String(line.cost) : "");
    setCostMode("net"); // We store net; if user toggles gross, BE derives.
    setVatTreatment(line.vat_treatment || "standard");
    setVendorName(line.vendor_name || "");
    axios
      .get(`${API}/stock/field-suggestions`)
      .then(r => setVendorSuggestions(r.data?.vendors || []))
      .catch(() => setVendorSuggestions([]));
    setPurchaseDate(line.purchase_date || "");
    setInitialQtyDisplay(toDisplayQty(line.initial_quantity, baseUnit));
    setConfirmingDelete(false);
    setRecostSummary(null);
    setSanityOverride(false);
    setBaseline(null);

    // Fetch this product's historical cost baseline. Silently no-op on
    // failure — the sanity check just doesn't render, which is exactly
    // the desired behaviour on a stale/offline network.
    if (line.product_id) {
      axios
        .get(`${API}/stock/products/${line.product_id}/cost-baseline`)
        .then((r) => setBaseline(r.data))
        .catch(() => setBaseline(null));
    }
  }, [open, line, baseUnit]);

  // Live cost-per-unit preview. Recomputes as either cost or quantity
  // is edited so the ambiguity that caused the Chameleon-film bug can
  // never happen silently again.
  const preview = useMemo(() => {
    const c = parseFloat(cost);
    const q = parseFloat(initialQtyDisplay);
    if (isNaN(c) || isNaN(q) || q <= 0) return null;
    const perUnit = c / q;
    return { perUnit, qty: q };
  }, [cost, initialQtyDisplay]);

  const handleSave = async () => {
    if (!line?.unit_id) return;
    const body = {};

    // Cost: send net or gross depending on the toggle. Backend derives
    // the other side. Non-empty required — clearing cost isn't allowed.
    const parsedCost = parseFloat(cost);
    if (!isNaN(parsedCost) && parsedCost > 0) {
      if (costMode === "gross") body.cost_gross = parsedCost;
      else body.cost = parsedCost;
    }
    if (vatTreatment) body.vat_treatment = vatTreatment;
    if (vendorName !== (line.vendor_name || "")) body.vendor_name = vendorName;
    if (purchaseDate !== (line.purchase_date || "")) body.purchase_date = purchaseDate;
    if (canEditQty) {
      const iq = toStorageQty(initialQtyDisplay, baseUnit);
      if (iq !== null && iq !== line.initial_quantity) body.initial_quantity = iq;
    }

    if (Object.keys(body).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${API}/stock/units/${line.unit_id}`, body);
      const downstream = res.data?.downstream;
      if (downstream?.invalidated_job_logs > 0) {
        toast.success(
          `Saved. ${downstream.invalidated_job_logs} job log(s) queued for re-costing on the next reconcile.`
        );
      } else {
        toast.success("Saved");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!line?.unit_id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/stock/units/${line.unit_id}`);
      // Fire the undo toast — user has 30 seconds to reverse the
      // delete. On restore, `onSaved` re-fetches the table so the row
      // reappears.
      showDeleteUndoToast({
        unitId: line.unit_id,
        productName: line.product_name,
        onRestored: () => onSaved?.(),
      });
      onDeleted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const handleRecostNow = async () => {
    if (!line?.unit_id) return;
    setRecosting(true);
    try {
      const res = await axios.post(`${API}/stock/units/${line.unit_id}/recost-now`);
      const mat = res.data?.material_costs || {};
      const prof = res.data?.job_profitability || {};
      const summary = {
        materialUpdates: mat.updated ?? mat.to_update ?? 0,
        marginUpdates: prof.updated ?? prof.to_update ?? 0,
      };
      setRecostSummary(summary);
      toast.success(
        `Re-costed: ${summary.materialUpdates} material entrie(s), ${summary.marginUpdates} margin(s) updated`
      );
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to re-cost");
    } finally {
      setRecosting(false);
    }
  };

  if (!line) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-lg"
        data-testid="edit-delivery-line-modal"
      >
        <DialogHeader>
          <DialogTitle>Edit delivery line</DialogTitle>
          <p className="text-slate-400 text-sm mt-1">
            {line.product_name || "Unknown product"}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Cost + mode toggle */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-white text-sm">
                Cost (per {baseUnit === "cm" ? "roll" : "unit"}, {costMode === "net" ? "net" : "gross"})
              </Label>
              <div className="flex items-center gap-1 text-xs">
                <button
                  onClick={() => setCostMode("net")}
                  className={`px-2 py-0.5 rounded ${costMode === "net" ? "bg-saturn-orange text-white" : "text-slate-400 hover:text-slate-200"}`}
                  data-testid="edit-cost-mode-net"
                >
                  Net
                </button>
                <button
                  onClick={() => setCostMode("gross")}
                  className={`px-2 py-0.5 rounded ${costMode === "gross" ? "bg-saturn-orange text-white" : "text-slate-400 hover:text-slate-200"}`}
                  data-testid="edit-cost-mode-gross"
                >
                  Gross
                </button>
              </div>
            </div>
            <Input
              type="number" min="0" step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder={costMode === "gross" ? "€ total (VAT-inclusive)" : "€ total (excl. VAT)"}
              className="bg-slate-800 border-slate-700 text-white mt-1"
              data-testid="edit-cost-input"
            />
            {preview && (
              <p
                className="text-emerald-400 text-xs mt-1 font-mono"
                data-testid="edit-cost-per-unit-preview"
              >
                = €{preview.perUnit.toFixed(2)} per {unitLabel} (over {preview.qty} {unitLabel}{preview.qty === 1 ? "" : "s"})
              </p>
            )}
            {/* Sanity check: is the new per-uom rate suspiciously low
                vs this product's own history? Auto-hides when the
                product has <3 prior costed deliveries (baseline
                endpoint returns `insufficient: true`). */}
            <div className="mt-2">
              <CostSanityWarning
                baseline={baseline}
                currentPerUom={preview?.perUnit}
                onOverride={() => setSanityOverride(true)}
                overridden={sanityOverride}
              />
            </div>
          </div>

          {/* Initial quantity (only for whole units) */}
          {canEditQty ? (
            <div>
              <Label className="text-white text-sm">
                Size ({baseUnit === "cm" ? "meters" : baseUnit === "ml" ? "ml" : "pcs"})
              </Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={initialQtyDisplay}
                onChange={(e) => setInitialQtyDisplay(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="edit-initial-qty-input"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-400 text-xs bg-amber-500/5 border border-amber-500/20 rounded p-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Size can&apos;t be edited — this unit has already been partially consumed.
                Only cost is editable now.
              </span>
            </div>
          )}

          {/* VAT treatment */}
          <div>
            <Label className="text-white text-sm">VAT treatment</Label>
            <Select value={vatTreatment} onValueChange={setVatTreatment}>
              <SelectTrigger
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="edit-vat-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                {VAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vendor + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white text-sm">Vendor</Label>
              <SuggestInput
                value={vendorName}
                onChange={setVendorName}
                suggestions={vendorSuggestions}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                testId="edit-vendor-input"
              />
            </div>
            <div>
              <Label className="text-white text-sm">Date</Label>
              <Input
                type="date"
                value={purchaseDate ? purchaseDate.slice(0, 10) : ""}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="edit-date-input"
              />
            </div>
          </div>

          {/* Consumed-unit re-cost affordance.
              After a cost correction on a unit that jobs have already
              consumed, the parent job_logs' material_cost + margin are
              invalidated automatically (nulled by the PATCH handler).
              The nightly reconcile at 03:00 Europe/Dublin picks them
              up, but this button fires the same backfills immediately
              so the admin sees corrected margins right after saving. */}
          {line.consumed && (
            <div className="bg-slate-800/50 border border-slate-700 rounded p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs text-slate-300 flex-1">
                  <p className="font-medium text-slate-100">
                    This unit has been consumed by past job(s).
                  </p>
                  <p className="text-slate-400 mt-0.5">
                    After a cost fix, those job margins will re-cost
                    automatically at 03:00 — or hit the button to
                    re-cost them right now.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleRecostNow}
                  disabled={recosting || saving || deleting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 whitespace-nowrap"
                  data-testid="edit-recost-now-btn"
                >
                  {recosting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Re-costing…</>
                  ) : (
                    <><Zap className="w-3.5 h-3.5 mr-1" /> Re-cost affected jobs now</>
                  )}
                </Button>
              </div>
              {recostSummary && (
                <p
                  className="text-emerald-400 text-xs font-mono"
                  data-testid="edit-recost-summary"
                >
                  ✓ {recostSummary.materialUpdates} material entrie(s),{" "}
                  {recostSummary.marginUpdates} job margin(s) updated
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Delete flow — two-step inline confirmation so it can't
              be fat-fingered on mobile. */}
          {!confirmingDelete ? (
            <Button
              variant="outline"
              onClick={() => setConfirmingDelete(true)}
              disabled={saving || deleting}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 sm:mr-auto"
              data-testid="edit-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete line
            </Button>
          ) : (
            <div className="flex gap-2 sm:mr-auto">
              <Button
                variant="outline"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="border-slate-600 text-slate-300"
                data-testid="edit-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="edit-delete-confirm-btn"
              >
                {deleting
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</>
                  : "Confirm delete"}
              </Button>
            </div>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving || deleting}
            className="border-slate-600 text-slate-300"
            data-testid="edit-close-btn"
          >
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || deleting}
            className="bg-saturn-orange hover:bg-saturn-orange/90 text-white"
            data-testid="edit-save-btn"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
              : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDeliveryLineModal;
